/**
 * Метрики использования интерфейса: буфер и отправка на свой api.
 *
 * Мимо apiClient намеренно: его перехватчик на 401 разлогинивает пользователя и пишет в консоль,
 * а телеметрия не имеет права влиять на работу приложения. По той же причине ошибки отправки молчат
 * и потерянная пачка не повторяется.
 *
 * Кто пользуется интерфейсом, сюда не попадает: установку определяет сервер, сессия — случайное
 * значение вкладки. Значение контрола передаётся только там, где это перечисление из кода.
 */
const baseUrl = process.env.REACT_APP_BACKEND_URL?.replace(/\/$/, '') ?? '';
const USAGE_EVENTS_URL = `${baseUrl}/api/usage-events`;
const SESSION_STORAGE_KEY = 'callcenter::usageSession';
const LOGIN_SECTION = 'login';
const FLUSH_INTERVAL_MS = 10_000;
const FLUSH_SIZE = 30;
// 100 событий укладываются в 32 КБ при лимите keepalive в 64 КБ: длины полей ограничивает сервер.
const MAX_BATCH_SIZE = 100;

export const MAX_BUFFERED_EVENTS = 200;
export const SECTION_VIEW_EVENT = 'section.view';

let buffer = [];
// null — ответ о флаге ещё не получен: события копятся, в том числе на экране входа.
let enabled = null;
let section = LOGIN_SECTION;
let flushTimer = null;
let memorySessionId = null;

const generateSessionId = () =>
  Array.from(crypto.getRandomValues(new Uint8Array(8)), (byte) =>
    byte.toString(16).padStart(2, '0')
  ).join('');

const getSessionId = () => {
  try {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (stored) {
      return stored;
    }
    const created = generateSessionId();
    sessionStorage.setItem(SESSION_STORAGE_KEY, created);
    return created;
  } catch {
    memorySessionId = memorySessionId ?? generateSessionId();
    return memorySessionId;
  }
};

const clearFlushTimer = () => {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
};

const scheduleFlush = () => {
  if (flushTimer === null) {
    flushTimer = setTimeout(flushUsageEvents, FLUSH_INTERVAL_MS);
  }
};

/**
 * Отправляет накопленное. Без флага ждёт: флаг приходит только после входа (его читает FeaturesProvider
 * в авторизованной ветке), поэтому события экрана входа уходят уже с сессией.
 */
export function flushUsageEvents() {
  clearFlushTimer();

  if (enabled !== true || buffer.length === 0) {
    return;
  }

  const batch = buffer.slice(0, MAX_BATCH_SIZE);
  buffer = buffer.slice(MAX_BATCH_SIZE);

  fetch(USAGE_EVENTS_URL, {
    method: 'POST',
    keepalive: true,
    // Сессия — в cookie; include нужен, когда api на другом адресе, чем интерфейс.
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sessionId: getSessionId(), appVersion: __APP_VERSION__, events: batch })
  }).catch(() => {});

  if (buffer.length > 0) {
    scheduleFlush();
  }
}

/**
 * @param {string} name id контрола
 * @param {string} [value] значение из перечисления; текст и идентификаторы передавать нельзя
 */
export const trackEvent = (name, value) => {
  if (enabled === false) {
    return;
  }

  const event = { name, section, ts: new Date().toISOString(), ...(value ? { value } : {}) };
  buffer = [...buffer, event].slice(-MAX_BUFFERED_EVENTS);

  if (enabled !== true) {
    return;
  }
  if (buffer.length >= FLUSH_SIZE) {
    flushUsageEvents();
    return;
  }
  scheduleFlush();
};

export const trackSectionView = (nextSection) => {
  section = nextSection;
  trackEvent(SECTION_VIEW_EVENT);
};

/** Ответ о флаге. Выключено — накопленное выбрасывается, а новые события не копятся. */
export const setUsageEventsEnabled = (isEnabled) => {
  enabled = Boolean(isEnabled);

  if (enabled) {
    flushUsageEvents();
    return;
  }
  clearFlushTimer();
  buffer = [];
};

/**
 * Вызывается при выходе: следующий вход начинает с чистого состояния. Сессия к этому моменту уже закончена,
 * поэтому остаток, если он есть, сервер отвергнет, и ошибка отправки промолчит: терять его допустимо.
 */
export const resetUsageEvents = () => {
  flushUsageEvents();
  clearFlushTimer();
  buffer = [];
  enabled = null;
  section = LOGIN_SECTION;
};

/**
 * Обработчик onValueChange для Select, Tabs и RadioGroup: у их Root нет DOM-события,
 * которое увидел бы общий слушатель.
 */
export const trackedValueChange = (trackId, sendValue, onValueChange) => {
  if (!trackId) {
    return onValueChange;
  }

  return (value) => {
    trackEvent(trackId, sendValue ? value : undefined);
    onValueChange?.(value);
  };
};
